import { pipe } from 'fp-ts/function';
import { array, option } from 'fp-ts';
import { Form } from '@mews/framework';
import { createFormResolver } from '@mews-commander/form';
import { OverlayedLoader } from '@mews-commander/ui';
import { TextMessagingConfiguration } from '@mews-commander/data-access';
import { validation as validate } from '@mews-commander/legacy-form';
import { useTextMessagingCofigurationUpdate } from '../queries';
import { GeneralFormFields } from './GeneralFormFields';
import { GeneralFormValues } from './types';
import { getConstraints } from './constraints';

interface GeneralFormContainerContainerProps {
    enterpriseId: string;
    configuration: TextMessagingConfiguration;
    hasSenderNumber: boolean;
}

export const GeneralFormContainer = ({
    configuration,
    enterpriseId,
    hasSenderNumber,
}: GeneralFormContainerContainerProps) => {
    const { supportedCountries, senderPhoneNumber } = configuration;
    const { isPending, mutate } = useTextMessagingCofigurationUpdate({ enterpriseId });

    const defaultCountries = pipe(
        supportedCountries,
        array.filterMap((country) => (country.isExcluded ? option.some(country.countryCode) : option.none))
    );

    const handleSubmit = (values: GeneralFormValues) => {
        const { countries, shortName } = values;
        const formCountries = new Set(countries);

        const textMessageCountries = supportedCountries.map(({ countryCode }) => ({
            countryCode,
            isExcluded: formCountries.has(countryCode),
        }));

        mutate({
            enterpriseId,
            textMessageCountries: {
                value: textMessageCountries,
            },
            shortName: {
                value: shortName,
            },
        });
    };

    const constraints = getConstraints({
        shortName: {
            maxLength: configuration.shortName.maxLength,
            validationRegex: configuration.shortName.validationRegex,
        },
    });

    const resolver = createFormResolver<GeneralFormValues>(validate(constraints));

    const defaultValues = {
        countries: defaultCountries,
        shortName: configuration.shortName.value ?? '',
    };

    const formDefaultValues = hasSenderNumber ? { ...defaultValues, senderPhoneNumber } : defaultValues;

    return (
        <>
            <OverlayedLoader show={isPending} fixed />
            <Form onSubmit={handleSubmit} resolver={resolver} mode="onTouched" defaultValues={formDefaultValues}>
                {({ submit }) => (
                    <GeneralFormFields
                        hasSenderNumber={hasSenderNumber}
                        configuration={configuration}
                        submit={submit}
                    />
                )}
            </Form>
        </>
    );
};
